import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";

import ApplicationForm from "../Components/ApplicationForm";
import Button from "../Components/buttons/Button";
import FormHtmlText from "../Components/forms/FormHtmlText";
import FormText from "../Components/forms/FormText";
import H2 from "../Components/forms/H2";
import JobAdsList from "../Components/JobAdsList";
import { CompanyContext } from "../contexts/CompanyContext";
import { getCompany, getCompanyJobAds } from "../lib/api";
import { JobAd } from "../lib/models";
import LoadingView from "./LoadingView";
import JobAdDetails from "../Components/JobAdDetails";
import Hero from "../Components/Hero";
import ArrowIcon from "../Components/ArrowIcon";

const AdView: React.FC<{ previewMode: boolean }> = ({ previewMode }) => {
  const navigate = useNavigate();
  const applicationFormRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation(["AdView"]);
  const { jobAdId, companyId } = useParams();
  const [searchParams] = useSearchParams();
  const openForm = searchParams.get('openForm') === 'true'
  const {
    jobAds,
    company,
    activeJobAd,
    setActiveJobAd,
    setJobAds,
    setCompany,
  } = useContext(CompanyContext);
  const [applicationMode, setApplicationMode] = useState<boolean>(false);

  const getThreeRandomAds = (ads: JobAd[]): JobAd[] => {
    const arrayCopy = ads.filter((ad) => ad.jobAdId !== jobAdId).slice();
    const numElements = 3;
    for (let i = arrayCopy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arrayCopy[i], arrayCopy[j]] = [arrayCopy[j], arrayCopy[i]];
    }
    return arrayCopy.slice(0, numElements);
  };

  const useCompanyData = (companyId: string | undefined) => {
    return useQuery({
      queryKey: ["company", companyId],
      queryFn: async () => {
        if (companyId) {
          const company = await getCompany(companyId);
          return company;
        }
        return null;
      },
      enabled: !!companyId && !company,
    });
  };

  const useJobAdsData = (companyId: string | undefined) => {
    return useQuery({
      queryKey: ["jobAds", companyId, previewMode, jobAdId],
      queryFn: async () => {
        if (companyId) {
          const jobAds = await getCompanyJobAds(
            companyId,
            previewMode,
            jobAdId
          );
          return jobAds;
        }
        return null;
      },
      enabled: !activeJobAd && !!companyId && !!jobAdId,
    });
  };

  const {
    data: companyData,
    error: companyError,
    isLoading: companyLoading,
  } = useCompanyData(companyId);
  const {
    data: jobAdsData,
    error: jobAdsError,
    isLoading: jobAdsLoading,
  } = useJobAdsData(companyId);

  useEffect(() => {
    if (companyData) {
      setCompany(companyData);
    }
    if (jobAdsData) {
      const foundJobAd = jobAdsData.find((ad) => ad.jobAdId === jobAdId);
      if (foundJobAd) {
        setActiveJobAd(foundJobAd);
      }
      setJobAds(jobAdsData);
    }
    if (openForm) {
      setTimeout(() => {
        setApplicationMode(true);
        applicationFormRef.current?.scrollIntoView({
          behavior: "smooth",
        });
      }, 750)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData, jobAdsData, openForm]);

  const handleClick = () => {
    setApplicationMode(true);
    applicationFormRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  const handleHeroClick = () => {
    navigate(`/${company?.name}/${company?.companyId}`, {
      state: { targetId: "jobAdsList" },
    });
  };

  return (
    <div>
      {companyLoading || jobAdsLoading || companyError || jobAdsError ? (
        <>
          {companyError || jobAdsError ? (
            <div className="mt-20 text-center">
              <FormText
                text={t("common:somethingWentWrong")}
                color="text-warning"
              />
            </div>
          ) : (
            <LoadingView />
          )}
        </>
      ) : (
        <>
          {activeJobAd ? (
            <>
              <Hero
                onButtonClick={handleHeroClick}
                image={{
                  src: activeJobAd?.backgroundImageUrl || "",
                  alt: activeJobAd?.title || "",
                  effect: true,
                }}
                company={company}
              />
              <JobAdDetails
                jobAd={activeJobAd}
                company={company}
                showButton={true}
                onClick={handleClick}
                previewMode={previewMode}
              />
              <div className="max-w-xl m-4 mt-10 sm:mx-auto">
                <div className="mb-2">
                  <FormText
                    color="text-primaryHover"
                    bold
                    text={`${t("AdView:lastDate")}: ${
                      activeJobAd.lastApplicationDate
                        ? new Date(
                            activeJobAd.lastApplicationDate
                          ).toLocaleDateString("sv-SE", {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                          })
                        : ""
                    }`}
                  />
                </div>
                <FormHtmlText
                  text={(activeJobAd?.description || "") as unknown as string}
                />
                {!applicationMode && !previewMode && (
                  <div className="mt-10">
                    <Button
                      text={t("ApplicationView:apply")}
                      onClick={handleClick}
                    />
                  </div>
                )}
              </div>
              <div ref={applicationFormRef}>
                {applicationMode && jobAdId && !previewMode && (
                  <ApplicationForm jobAdId={jobAdId} />
                )}
              </div>
            </>
          ) : (
            <>
              <Hero
                onButtonClick={handleHeroClick}
                image={{
                  src: "",
                  alt: "",
                  effect: true,
                }}
                company={company}
              />
              <div className="flex justify-center w-full mt-14">
                <p className="text-xl font-semibold">
                  {t("AdView:notAvailable")}
                </p>
              </div>
            </>
          )}
          <div className="flex flex-col items-center justify-center mx-auto mt-5 sm:mt-10 max-w-5xl px-4 md:px-8">
            <div className="w-full space-y-5 mt-4 text-center">
              <div className="text-center">
                <H2 text={t("AdView:moreJobs")} />
              </div>

              {jobAds && company && (
                <JobAdsList
                  jobAds={getThreeRandomAds(jobAds)}
                  company={company}
                />
              )}

              <div className="flex items-center justify-center">
                <div className="inline-flex py-2">
                  <Button
                    variant="contained-inverted"
                    icon={<ArrowIcon />}
                    text={t("AdView:allJobs")}
                    onClick={() =>
                      navigate(`/${company?.name}/${company?.companyId}`, {
                        state: { targetId: "jobAdsList" },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdView;
